export type LLMProvider = 'openrouter' | 'openai' | 'ollama' | 'offline';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface LLMResponse {
  content: string;
  provider: LLMProvider;
  model: string;
  success: boolean;
  error?: string;
  audio?: string; // base64 encoded audio from backend
  phonemes?: Array<{ phoneme: string, start: number, end: number }>; // phoneme timing data
}

export class LLMProviderService {
  private provider: LLMProvider;
  //private apiKey?: string;
  private model: string;
  private fallbackMessage: string;

  constructor() {
    // Determine provider based on environment variables
    const apiUrl = import.meta.env.VITE_API_URL;
    const offline = import.meta.env.VITE_OFFLINE_MODEL_ENABLED;


    if (apiUrl  && offline !== 'true' ) {
      this.provider = 'ollama';
      this.model = import.meta.env.VITE_LLM_MODEL || 'hf.co/cstr/salamandra-7b-instruct-GGUF:F32';
    } else {
      this.provider = 'offline';
      this.model = import.meta.env.VITE_WEBLLM_MODEL || 'Llama-3.2-1B-Instruct-q4f32_1';
    }

    this.fallbackMessage = import.meta.env.VITE_FALLBACK_MESSAGE ||
      'Lo siento, estoy teniendo problemas de conexión. Por favor, intenta de nuevo más tarde.';
  }

  async chat(messages: ChatMessage[],sessionId?:string|null,avatarId?:string|null, turnCount?:number): Promise<LLMResponse> {
    try {
      switch (this.provider) {
        case 'ollama':
          return await this.chatOllama(messages,sessionId,avatarId,turnCount);
        case 'offline':
          return await this.chatOffline(messages);
        default:
          throw new Error(`Unknown provider: ${this.provider}`);
      }
    } catch (error) {
      console.error(`LLM Provider Error (${this.provider}):`, error);
      return {
        content: this.fallbackMessage,
        provider: this.provider,
        model: this.model,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private async chatOllama(messages: ChatMessage[],sessionId?:string|null, avatarId?:string|null, turnCount?:number): Promise<LLMResponse> {
    const apiUrl = import.meta.env.VITE_API_URL;
    const chatEndpoint = import.meta.env.VITE_STREAM_RESPONSES ? 'chat_stream' : 'chat' ;
    
    const response = await fetch(`${apiUrl}/${chatEndpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        avatarId:avatarId,
        sessionId:sessionId,
        turnCount: turnCount,
        text: messages[messages.length - 1].content,
        history: messages.slice(0, -1).map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      })
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Extract response text - handle both old and new format
    const content = data.ollama_response || data.response || data.data?.text || '';

    // Extract audio and phonemes if available (new enhanced backend format)
    let audio: string | undefined;
    let phonemes: Array<{ phoneme: string, start: number, end: number }> | undefined;

    if (data.data) {
      // New format: data.audio.base64 or data.audio.url
      if (data.data.audio) {
        audio = data.data.audio.base64 || data.data.audio.url || data.data.audio;
      }
      // New format: data.phonemes
      if (data.data.phonemes) {
        phonemes = data.data.phonemes;
      }
    } else {
      // Legacy format: direct audio and phonemes at root level
      audio = data.audio;
      phonemes = data.phonemes;
    }

    return {
      content,
      provider: 'ollama',
      model: this.model,
      success: true,
      audio,
      phonemes
    };
  }

  private async chatOffline(messages: ChatMessage[]): Promise<LLMResponse> {
    // Check if WebLLM is available
    if (typeof window !== 'undefined' && (window as any).webllm) {
      const webllm = (window as any).webllm;
      const prompt = this.formatMessagesForWebLLM(messages);
      console.log(prompt)
      const response = await webllm.generate(prompt);

      console.log(response)
      return {
        content: response,
        provider: 'offline',
        model: this.model,
        success: true
      };
    }

    // If WebLLM not available, return fallback
    return {
      content: this.fallbackMessage,
      provider: 'offline',
      model: this.model,
      success: false,
      error: 'Offline model not loaded'
    };
  }

  private formatMessagesForWebLLM(messages: ChatMessage[]): string {
    // Simple format for WebLLM - concatenate messages
    return messages.map(msg => `${msg.role === 'user' ? 'Human' : 'Assistant'}: ${msg.content}`).join('\n');
  }

  getProvider(): LLMProvider {
    return this.provider;
  }

  getModel(): string {
    return this.model;
  }

  isOfflineCapable(): boolean {
    return this.provider === 'offline' || import.meta.env.VITE_OFFLINE_MODEL_ENABLED === 'true';
  }
}